"use client";

import { useState } from "react";
import {
  User,
  UserPlus,
  Mail,
  Newspaper,
  CalendarClock,
  Languages,
  BarChart3,
  ScrollText,
  Settings as SettingsIcon,
  ChevronRightIcon,
  RefreshCw,
} from "lucide-react";
import { MailboxScreen } from "../mailbox";
import { NewsScreen } from "../news";
import { ChatMessagesScreen } from "../chat-messages";
import { LanguagesScreen } from "../languages";
import { ChartsSettingsScreen } from "../charts-settings";
import { JournalScreen } from "../journal";
import { SettingsDetailsScreen } from "../settings-details";
import { EconomicCalendarScreen } from "../economic-calendar";
import { AccountSelectorScreen } from "../account-selector";
import { VybeAccountScreen } from "../vybe-account";
import { useVybeAuthStore } from "@/lib/vybe-auth";
import {
  useAccountsStore,
  useActiveAccount,
  getGatewayLabel,
} from "@/lib/accounts/store";
import { useConnectionProgressStore } from "@/vybe-trader-client/connection-progress/stores/useConnectionProgressStore";
import { useAccountInformationStore } from "@/vybe-trader-client/account-information/stores/useAccountInformationStore";
import { useMailboxStore } from "@/vybe-trader-client/mailbox/stores/useMailboxStore";
import { useSettingsStore, LANGUAGES } from "@/lib/settings";
import { BrokerLogo, GatewayChip } from "@/components/common";

interface SettingItem {
  id: string;
  glyph: React.ElementType;
  title: string;
  /** Current value shown inline on the right (desktop settings idiom). */
  value?: string;
  /** Custom trailing slot (e.g. live status dot + label) rendered before the chevron. */
  trailing?: React.ReactNode;
  group: "ACCOUNT" | "RESEARCH" | "TERMINAL";
}

const SETTING_GROUPS: Array<SettingItem["group"]> = [
  "ACCOUNT",
  "RESEARCH",
  "TERMINAL",
];

export default function SettingsScreen() {
  const [addAccountOnOpen, setAddAccountOnOpen] = useState(false);
  const [showMailbox, setShowMailbox] = useState(false);
  const [showChatMessages, setShowChatMessages] = useState(false);
  const [showLanguages, setShowLanguages] = useState(false);
  const [showChartsSettings, setShowChartsSettings] = useState(false);
  const [showJournal, setShowJournal] = useState(false);
  const [showSettingsDetails, setShowSettingsDetails] = useState(false);
  const [showEconomicCalendar, setShowEconomicCalendar] = useState(false);
  const [showNews, setShowNews] = useState(false);
  const [showAccountSelector, setShowAccountSelector] = useState(false);
  const [showVybeAccount, setShowVybeAccount] = useState(false);

  // Vybe Trader session + presence state for the ACCOUNT row (proposal 55)
  const vybeSignedIn = useVybeAuthStore((s) => !!s.token);
  const presenceConnected = useVybeAuthStore((s) => s.presenceConnected);

  // Get data from stores
  const activeAccount = useActiveAccount();
  const accountsCount = useAccountsStore((state) => state.accounts.length);
  const { getSetting } = useSettingsStore();

  // Get connection status
  const isConnected = useConnectionProgressStore((state) => state.isConnected);
  const isConnecting = useConnectionProgressStore((state) => state.isConnecting);
  const requestReconnect = useAccountsStore((state) => state.requestReconnect);

  // Live account vitals (balance / equity / leverage / currency)
  const accountInfo = useAccountInformationStore((state) => state.account);

  // Mailbox unread count for the inline value
  const unreadCount = useMailboxStore(
    (state) => state.messages.filter((m) => m.isUserUnread).length
  );

  // Get the display name for the selected language
  const getLanguageDisplayName = () => {
    const languageId = getSetting("language");
    const language = LANGUAGES.find((lang) => lang.id === languageId);
    return language ? language.name : "English";
  };

  if (showAccountSelector) {
    return (
      <AccountSelectorScreen
        onBack={() => {
          setShowAccountSelector(false);
          setAddAccountOnOpen(false);
        }}
        initialAddAccount={addAccountOnOpen}
      />
    );
  }

  if (showVybeAccount) {
    return <VybeAccountScreen onBack={() => setShowVybeAccount(false)} />;
  }

  if (showMailbox) {
    return <MailboxScreen onBack={() => setShowMailbox(false)} />;
  }

  if (showChatMessages) {
    return <ChatMessagesScreen onBack={() => setShowChatMessages(false)} />;
  }

  if (showLanguages) {
    return <LanguagesScreen onBack={() => setShowLanguages(false)} />;
  }

  if (showChartsSettings) {
    return <ChartsSettingsScreen onBack={() => setShowChartsSettings(false)} />;
  }

  if (showJournal) {
    return <JournalScreen onBack={() => setShowJournal(false)} />;
  }

  if (showSettingsDetails) {
    return (
      <SettingsDetailsScreen onBack={() => setShowSettingsDetails(false)} />
    );
  }

  if (showEconomicCalendar) {
    return (
      <EconomicCalendarScreen onBack={() => setShowEconomicCalendar(false)} />
    );
  }

  if (showNews) {
    return <NewsScreen onBack={() => setShowNews(false)} />;
  }

  const settingItems: SettingItem[] = [
    {
      id: "vybe-account",
      glyph: User,
      title: "Vybe Trader",
      trailing: (
        <span
          className={`font-mono text-[10.5px] font-medium ${
            vybeSignedIn && presenceConnected
              ? "text-green-500"
              : vybeSignedIn
                ? "text-orange-500"
                : "text-muted-foreground"
          }`}
        >
          ●{" "}
          {vybeSignedIn
            ? presenceConnected
              ? "connected"
              : "offline"
            : "sign in"}
        </span>
      ),
      group: "ACCOUNT",
    },
    { id: "new-account", glyph: UserPlus, title: "New Account", group: "ACCOUNT" },
    {
      id: "mailbox",
      glyph: Mail,
      title: "Mailbox",
      value: unreadCount > 0 ? `${unreadCount} unread` : undefined,
      group: "ACCOUNT",
    },
    { id: "news", glyph: Newspaper, title: "News", group: "RESEARCH" },
    {
      id: "tradays",
      glyph: CalendarClock,
      title: "Economic Calendar",
      group: "RESEARCH",
    },
    {
      id: "interface",
      glyph: Languages,
      title: "Interface",
      value: getLanguageDisplayName(),
      group: "TERMINAL",
    },
    { id: "charts", glyph: BarChart3, title: "Charts", group: "TERMINAL" },
    { id: "journal", glyph: ScrollText, title: "Journal", group: "TERMINAL" },
    { id: "settings", glyph: SettingsIcon, title: "Settings", group: "TERMINAL" },
  ];

  const handleItemClick = (itemId: string) => {
    switch (itemId) {
      case "vybe-account":
        setShowVybeAccount(true);
        break;
      case "new-account":
        setAddAccountOnOpen(true);
        setShowAccountSelector(true);
        break;
      case "mailbox":
        setShowMailbox(true);
        break;
      case "news":
        setShowNews(true);
        break;
      case "tradays":
        setShowEconomicCalendar(true);
        break;
      case "chat":
        setShowChatMessages(true);
        break;
      case "interface":
        setShowLanguages(true);
        break;
      case "charts":
        setShowChartsSettings(true);
        break;
      case "journal":
        setShowJournal(true);
        break;
      case "settings":
        setShowSettingsDetails(true);
        break;
    }
  };

  // Vitals row — "—" for never-connected accounts (proposal 12 empty-state idiom)
  const hasVitals = Boolean(activeAccount && isConnected && accountInfo);
  const fmtMoney = (v: number | undefined | null) =>
    typeof v === "number"
      ? v.toLocaleString("en-US", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : "—";
  const vitals = [
    { k: "Balance", v: hasVitals ? fmtMoney(accountInfo?.balance) : "—" },
    { k: "Equity", v: hasVitals ? fmtMoney(accountInfo?.equity) : "—" },
    {
      k: "Leverage",
      v: hasVitals && accountInfo?.leverage ? `1:${accountInfo.leverage}` : "—",
    },
    { k: "Ccy", v: hasVitals && accountInfo?.currency ? accountInfo.currency : "—" },
  ];

  return (
    <div className="h-full bg-background text-foreground flex flex-col">
      {/* Header according to developer-guidelines */}
      <div className="flex items-center justify-center px-4 py-3 h-14 bg-background border-b border-border">
        <h1 className="text-xl font-medium text-foreground">Settings</h1>
      </div>

      <div className="flex-1 overflow-y-auto">
        {/* Account header card — logo · login · status dot · gateway chip · live vitals */}
        <div className="px-3 pt-3">
          <div className="rounded-[10px] border border-border overflow-hidden">
            <button
              type="button"
              onClick={() => setShowAccountSelector(true)}
              className="w-full flex items-center gap-2.5 px-3 py-2.5 border-b border-border/60 hover:bg-muted/40 text-left"
            >
              {activeAccount ? (
                <BrokerLogo
                  companyName={activeAccount.companyName}
                  logoUrl={activeAccount.brokerLogoUrl}
                  className="w-7 h-7"
                  logoClassName="w-5 h-5"
                />
              ) : (
                <div className="w-7 h-7 rounded-md border border-border bg-muted/40 flex items-center justify-center shrink-0">
                  <UserPlus className="h-3.5 w-3.5 text-muted-foreground" />
                </div>
              )}
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="font-mono text-[12.5px] font-semibold text-foreground truncate">
                    {activeAccount
                      ? activeAccount.credentials.login
                      : accountsCount > 0
                        ? `No active account (${accountsCount} available)`
                        : "No active account"}
                  </span>
                  {activeAccount && (
                    <span
                      className={`w-[7px] h-[7px] rounded-full shrink-0 ${
                        isConnected ? "bg-green-500" : "bg-red-500"
                      }`}
                      aria-label={isConnected ? "Connected" : "Disconnected"}
                    />
                  )}
                </div>
                <div className="text-[10.5px] text-muted-foreground truncate mt-px">
                  {activeAccount
                    ? `${activeAccount.companyName} · ${activeAccount.serverName}`
                    : "Sign in to add a trading account"}
                </div>
              </div>
              {activeAccount && <GatewayChip label={getGatewayLabel(activeAccount)} />}
              <ChevronRightIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            </button>
            <div className="flex px-3 py-2 font-mono tabular-nums text-[10.5px]">
              {vitals.map((cell, i) => (
                <div key={cell.k} className={i === vitals.length - 1 ? "flex-[0.7]" : "flex-1"}>
                  <div className="text-[9px] uppercase tracking-wider text-muted-foreground">
                    {cell.k}
                  </div>
                  <div className="text-foreground mt-px truncate">{cell.v}</div>
                </div>
              ))}
            </div>
            {/* Trading link down: say so and offer the way back. Before this the only way to re-dial a
                stuck account was to relaunch the app — re-tapping it in the picker was a no-op. */}
            {activeAccount && !isConnected && (
              <button
                type="button"
                onClick={() => requestReconnect()}
                className="w-full flex items-center justify-between px-3 h-8 border-t border-border/60 bg-red-500/5 hover:bg-red-500/10 font-mono text-[10.5px]"
              >
                <span className="text-red-400">
                  {isConnecting ? "Connecting to trading server…" : "Trading server disconnected"}
                </span>
                <span className="inline-flex items-center gap-1 text-blue-400">
                  <RefreshCw className={`h-3 w-3 ${isConnecting ? "animate-spin" : ""}`} />
                  Reconnect
                </span>
              </button>
            )}
          </div>
        </div>

        {/* Settings list — grouped dense rows, neutral glyphs */}
        <div className="px-3 pt-3 pb-4">
          <div className="rounded-[10px] border border-border overflow-hidden">
            {SETTING_GROUPS.map((group, gIdx) => (
              <div key={group}>
                <div
                  className={`flex items-center h-6 px-3 bg-muted/30 border-b border-border/60 font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground ${
                    gIdx > 0 ? "border-t border-t-border" : ""
                  }`}
                >
                  {group}
                </div>
                {settingItems
                  .filter((item) => item.group === group)
                  .map((item) => (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => handleItemClick(item.id)}
                      className="w-full flex items-center gap-2.5 h-9 px-3 border-b border-border/40 last:border-b-0 hover:bg-muted/40 text-left text-[12.5px] text-foreground"
                    >
                      <item.glyph className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                      <span className="flex-1 truncate">{item.title}</span>
                      {item.value && (
                        <span className="font-mono text-[10.5px] text-muted-foreground truncate max-w-[40%]">
                          {item.value}
                        </span>
                      )}
                      {item.trailing}
                      <ChevronRightIcon className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                    </button>
                  ))}
              </div>
            ))}
          </div>
        </div>

        {/* Bottom spacing */}
        <div className="h-6" />
      </div>
    </div>
  );
}
